<template>
  <m-dropdown
    class="bucket-selector"
    :relocate-key="selectedBucketType"
    placement="bottomRight"
    :title="$t('gridPageChartEditor.bucket')"
    @hide="handleHide"
  >
    <slot
      name="trigger"
      :display-value="displayVal"
    >
      <m-focusable
        type="clickable"
        full-width
      >
        {{ displayVal }}
      </m-focusable>
    </slot>
    <template #overlay>
      <m-card
        class="_bucket-selector-overlay"
        padding-xs
      >
        <m-form-item :label="$t('bucketSelector.bucketType')">
          <m-select
            v-model:value="selectedBucketType"
            :items="bucketTypes"
            full-width
            match-trigger-width
            @change="handleChange"
          />
        </m-form-item>
        <template v-if="selectedBucketType === 'custom'">
          <m-divider small />
          <div class="_min-max-bucket-size">
            <div class="_min-max-bucket-size-inputs">
              <m-form-item
                :label="$t('bucketSelector.minValue')"
                class="_form-item"
              >
                <m-input-number
                  v-model:value="minValue"
                  :placeholder="$t('bucketSelector.minValue')"
                  full-width
                  :has-error="invalidMinOrMax"
                  @change="handleChange"
                />
              </m-form-item>
              <m-form-item
                :label="$t('bucketSelector.maxValue')"
                class="_form-item"
              >
                <m-input-number
                  v-model:value="maxValue"
                  :placeholder="$t('bucketSelector.maxValue')"
                  full-width
                  :has-error="invalidMinOrMax"
                  @change="handleChange"
                />
              </m-form-item>
            </div>
            <div
              v-if="invalidMinOrMax"
              class="_error"
            >
              {{ $t('bucketSelector.minValueError') }}
            </div>
          </div>
          <m-form-item :label="$t('bucketSelector.bucketSize')">
            <m-tooltip
              :force-show="(invalidBucketSize || tooManyBuckets)"
              :relocate-key="bucketSize"
              :disabled="!(invalidBucketSize || tooManyBuckets)"
              placement="left"
            >
              <m-input-number
                v-model:value="bucketSize"
                :placeholder="$t('bucketSelector.bucketSize')"
                full-width
                :has-error="invalidBucketSize || tooManyBuckets"
                @change="handleChange"
              />
              <template #title>
                <template
                  v-if="invalidBucketSize"
                >
                  {{ $t('bucketSelector.bucketSizeError') }}
                </template>
                <template
                  v-else-if="tooManyBuckets"
                >
                  {{ $t('bucketSelector.tooManyBucketsError', { max: MAX_BUCKETS }) }}
                </template>
              </template>
            </m-tooltip>
          </m-form-item>
        </template>
      </m-card>
    </template>
  </m-dropdown>
</template>

<script setup>
import { MAX_BUCKETS } from '@/composables/grid-page/chart/custom-buckets';
import { bucketType } from 'shared/constants.json';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  value: {
    type: Object,
    default: () => null,
  },
  result: {
    type: Object,
    default: () => null,
  },
});

const emit = defineEmits(['update:value']);

const handleChange = () => {
  if (hasErrors.value) {
    return;
  }
  emit('update:value', toStore.value);
};

const handleHide = () => {
  if (hasErrors.value) {
    minValue.value = props.value.min ?? DEFAULT_MIN;
    maxValue.value = props.value.max ?? DEFAULT_MAX;
    bucketSize.value = props.value.bucketSize ?? DEFAULT_BUCKET_SIZE;
    emit('update:value', props.value);
  }
};

const bucketTypes = ref([
  { text: t('bucketSelector.automatic'), value: bucketType.automatic },
  { text: t('bucketSelector.custom'), value: bucketType.custom },
]);

const DEFAULT_MIN = 0;
const DEFAULT_MAX = 100;
const DEFAULT_BUCKET_SIZE = 20;

const selectedBucketType = ref(bucketType.automatic);
const minValue = ref(DEFAULT_MIN);
const maxValue = ref(DEFAULT_MAX);
const bucketSize = ref(DEFAULT_BUCKET_SIZE);

if (props.value !== null) {
  selectedBucketType.value = props.value.bucketType ?? bucketType.automatic;
  minValue.value = props.value.min ?? DEFAULT_MIN;
  maxValue.value = props.value.max ?? DEFAULT_MAX;
  bucketSize.value = props.value.bucketSize ?? DEFAULT_BUCKET_SIZE;
}

const displayVal = computed(() => {
  if (selectedBucketType.value === bucketType.automatic) {
    return t('bucketSelector.automatic');
  }

  return t('bucketSelector.custom');
});

const invalidMinOrMax = computed(() => maxValue.value <= minValue.value);
const invalidBucketSize = computed(() => bucketSize.value <= 0);
const tooManyBuckets = computed(() => {
  if (invalidMinOrMax.value || invalidBucketSize.value) {
    return false;
  }
  return Math.ceil((maxValue.value - minValue.value) / bucketSize.value) > MAX_BUCKETS;
});
const hasErrors = computed(() => invalidMinOrMax.value || invalidBucketSize.value || tooManyBuckets.value);

const toStore = computed(() => ({
  bucketType: selectedBucketType.value,
  bucketSize: bucketSize.value,
  min: minValue.value,
  max: maxValue.value,
}));

watch(selectedBucketType, (newVal) => {
  if (newVal !== bucketType.custom) {
    return;
  }
  if (minValue.value === undefined || minValue.value === null) {
    minValue.value = DEFAULT_MIN;
  }
  if (maxValue.value === undefined || maxValue.value === null) {
    maxValue.value = DEFAULT_MAX;
  }
  if (bucketSize.value === undefined || bucketSize.value === null) {
    bucketSize.value = DEFAULT_BUCKET_SIZE;
  }
  handleChange();
});
</script>

<style scoped lang="scss">
.bucket-selector {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

._min-max-bucket-size {
  margin-bottom: 1rem;

  ._min-max-bucket-size-inputs {
    display: flex;
    gap: 0.5rem;
    justify-content: space-between;
    align-items: center;

    ._form-item {
      width: 100%;
      margin-bottom: 0;
    }
  }
}

  ._bucket-selector-overlay {
    min-width: 24rem;
  }
</style>
